import React, { useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Snackbar,
  Alert,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import client from "../../../API";               // <-- must expose users.confirm(id)

const MyTeam = () => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: "", sev: "success" });

  /* ─────────────────────────────── FETCH TEAM ─────────────────────────────── */
  const {
    data: teamData,
    isLoading,
    error,
  } = useQuery("team", client.users.fetchMyTeam);

  /* ─────────────────────────── ACTIVATE USER MUTATION ─────────────────────── */
  const activateMutation = useMutation(
    (userId) => client.users.confirm(userId),     // POST /users/<id>/confirm
    {
      onSuccess: () => {
        queryClient.invalidateQueries("team");
        setSnack({ open: true, msg: "User activated!", sev: "success" });
      },
      onError: () => {
        setSnack({ open: true, msg: "Activation failed.", sev: "error" });
      },
      onSettled: () => setDialogOpen(false),
    }
  );

  /* ───────────────────────────────── RENDER ───────────────────────────────── */
  if (isLoading) return <CircularProgress />;
  if (error)     return <Typography color="error">Error loading team data</Typography>;

  const companyName =
    teamData && teamData.length ? teamData[0].company_name : "";

  const openDialog = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  return (
    <>
      <Grid container spacing={2}>
        {teamData?.map((member) => (
          <Grid item xs={12} sm={6} md={4} key={member.id}>
            <Card sx={{ borderRadius: 3, boxShadow: 3, position: "relative" }}>
              {/* Whole card still links to profile */}
              <Link
                to={`/user/${member.id}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <CardContent
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    textAlign: "center",
                    p: 3,
                  }}
                >
                  <Avatar
                    src={member.phphref}
                    sx={{ width: 80, height: 80, mb: 1.5 }}
                  />
                  <Typography variant="h6">
                    {member.first_name} {member.last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {member.company_name}
                  </Typography>

                  {/* Status Chip */}
                  {member.status ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Inactive" color="warning" size="small" />
                  )}
                </CardContent>
              </Link>

              {/* Activate button (only when inactive) */}
              {!member.status && (
                <Button
                  variant="contained"
                  size="small"
                  color="primary"
                  sx={{ position: "absolute", top: 8, right: 8 }}
                  onClick={() => openDialog(member)}
                >
                  Activate
                </Button>
              )}
            </Card>
          </Grid>
        ))}

        {/* Plus card to add new member */}
        <Grid item xs={12} sm={6} md={4}>
          <Link
            to={`/register/${companyName}`}
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: 3,
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <AddIcon sx={{ fontSize: 40, mb: 1 }} />
                <Typography variant="body1">Add Team Member</Typography>
              </CardContent>
            </Card>
          </Link>
        </Grid>
      </Grid>

      {/* ───────────── Activation confirmation dialog ───────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Confirm team-member activation?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By confirming&nbsp;
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            , you authorize them to draw against your company’s shared credit
            line.
          </Typography>
          <Typography variant="caption" component="p">
            View full terms&nbsp;
            <MuiLink component={Link} to="/terms" target="_blank">
              here
            </MuiLink>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => activateMutation.mutate(selectedUser.id)}
            disabled={activateMutation.isLoading}
          >
            {activateMutation.isLoading ? "Activating..." : "Agree & Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyTeam;
